import {
  BarChart3,
  FileCheck,
  FileSignature,
  FileText,
  Target,
  Wrench,
  Zap,
} from "lucide-react";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSessionSwitcher } from "@/contexts/SessionSwitcherContext";
import { useGuideProgress } from "@/hooks/api/query/useGuideProgress";
import { useGetSectors } from "@/hooks/api/query/useGetSectors";
import { ClassificationBadge } from "@/components/ui/classification-badge";
import { CyFunLevelBadge } from "@/components/risk/CyFunLevelBadge";
import { Badge } from "@/components/ui/badge";
import { ExpandableDashboardCard } from "@/components/dashboard/ExpandableDashboardCard";
import { NotificareDetails } from "@/components/dashboard/NotificareDetails";
import { EvaluareRiscDetails } from "@/components/dashboard/EvaluareRiscDetails";
import { DecizieInregistrareDetails } from "@/components/dashboard/DecizieInregistrareDetails";
import { ComingSoonPlaceholder } from "@/components/dashboard/dashboardDetailPrimitives";
import { SectorMismatchAlert } from "@/components/dashboard/SectorMismatchAlert";
import { useGetDecizieInregistrare } from "@/hooks/api/query/useGetDecizieInregistrare";
import { useGetMaturityEvaluation } from "@/hooks/api/query/useGetMaturityEvaluation";
import { ImpactResultsTable } from "@/components/impact-analysis/ImpactResultsTable";
import { useOnboardingModal } from "@/contexts/OnboardingModalContext";
import { useImpactAnalysisModal } from "@/contexts/ImpactAnalysisModalContext";
import { useRiskAnalysisModal } from "@/contexts/RiskAnalysisModalContext";
import {
  mapGuideStatusToCardStatus,
  calculateAnalizaRiscProgress,
  getNotificationPdfChecks,
  getNotificationPdfStatusFromChecks,
  getNotificationPdfProgressFromChecks,
  getEfectPerturbatorStatus,
  getMaturityStatus,
  isRiskAnalysisUnlocked,
} from "@/utils/guideProgressCalculator";
import {
  getNotificareSectorNames,
  getDecizieSectorNames,
} from "@/utils/sectorMismatch";
import type { ClassificationType } from "@/types/entity";
import type { DashboardRowId } from "@/types/expandable-dashboard";

function StatusEntitateDashboard() {
  const navigate = useNavigate();
  const { currentSessionId } = useSessionSwitcher();
  const { steps, isLoading, entityData, riskData } =
    useGuideProgress(currentSessionId);
  const { data: sectors = [] } = useGetSectors();
  const decizieQuery = useGetDecizieInregistrare(currentSessionId);
  const { data: maturityEval } = useGetMaturityEvaluation(
    steps.step3.status === "completed" ? currentSessionId : undefined
  );
  const { showOnboardingModal } = useOnboardingModal();
  const { showImpactAnalysisModal } = useImpactAnalysisModal();
  const { showRiskAnalysisModal } = useRiskAnalysisModal();

  const [expandedRows, setExpandedRows] = useState<
    Record<DashboardRowId, boolean>
  >({
    notificare: false,
    "decizie-notificare": false,
    "efect-perturbator": false,
    risc: false,
    "decizie-risc": false,
    maturitate: false,
    "decizie-maturitate": false,
  });

  const toggleRow = useCallback((rowId: DashboardRowId) => {
    setExpandedRows((prev) => ({ ...prev, [rowId]: !prev[rowId] }));
  }, []);

  const notificationChecks = getNotificationPdfChecks(entityData);
  const notificationPdfStatus =
    getNotificationPdfStatusFromChecks(notificationChecks);

  const decizieStatus =
    notificationPdfStatus !== "completed"
      ? "neinceput"
      : decizieQuery.data
        ? "finalizat"
        : "in-asteptare";

  if (isLoading || decizieQuery.isLoading) {
    return (
      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-6xl mx-auto">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-ds-border-200 rounded w-1/3" />
            <div className="grid grid-cols-1 lg:grid-cols-[3fr_2fr] gap-5">
              {[...Array(7)].map((_, i) => (
                <div
                  key={i}
                  className="bg-white rounded-xl border border-ds-border-200 p-5 h-24"
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const step1Status = mapGuideStatusToCardStatus(
    notificationPdfStatus,
    "finalizat"
  );
  const step1Progress =
    getNotificationPdfProgressFromChecks(notificationChecks);

  const step3Status = mapGuideStatusToCardStatus(
    steps.step3.status,
    "finalizat"
  );
  const step3Progress = calculateAnalizaRiscProgress(riskData);

  const efPertStatus = getEfectPerturbatorStatus(entityData);
  const showEfectPerturbatorCard = efPertStatus.isVisible;
  const efectPerturbatorCardStatus = mapGuideStatusToCardStatus(
    efPertStatus.status
  );
  const shouldEfectPerturbatorNavigate =
    efPertStatus.status === "in_progress" ||
    efPertStatus.status === "not_started";
  const hasCompletedResult = efPertStatus.status === "completed";
  const isImpact =
    entityData?.nis_classification?.classificationV2 === "IMPACT";

  const isNotificareCompleted = isRiskAnalysisUnlocked(entityData);
  const shouldRiscNavigate =
    isNotificareCompleted && steps.step3.status === "not_started";

  const isRiskCompleted = steps.step3.status === "completed";
  const maturityProgress = (() => {
    if (!maturityEval || !isRiskCompleted) return undefined;
    if (maturityEval.eval_state === "FULL_COMPLETE") return 100;
    if (maturityEval.eval_state === "PRELIM_COMPLETE") return 25;
    const responses = maturityEval.preliminary_responses;
    if (!responses) return undefined;
    const totalItems = Object.keys(responses).length;
    if (totalItems === 0) return undefined;
    const checkedItems = Object.values(responses).filter(Boolean).length;
    if (checkedItems === 0) return undefined;
    return Math.round((checkedItems / totalItems) * 25);
  })();
  const maturityStatus = getMaturityStatus(maturityEval, isRiskCompleted);

  return (
    <div className="flex-1 overflow-y-auto md:p-6 p-4">
      <div className="max-w-6xl mx-auto">
        <div className="mb-6">
          <h1
            data-testid="dashboard-page-title"
            className="text-xl md:text-2xl font-bold font-heading text-navy-900"
          >
            Dashboard Conformitate NIS2
          </h1>
          <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1.5 text-sm md:text-base text-ds-text-500">
            <span>{entityData?.nume_entitate || "-"}</span>
            <span aria-hidden="true">•</span>
            <span>
              CUI:{" "}
              <span data-testid="dashboard-entity-cui" className="font-mono">
                {entityData?.cui || "-"}
              </span>
            </span>
            <span aria-hidden="true">•</span>
            <span className="inline-flex items-center gap-1.5">
              <span className="text-xs text-ds-text-500">Încadrare:</span>
              <ClassificationBadge
                size="sm"
                classification={
                  entityData
                    ? (entityData.nis_classification?.classificationV2 as
                        | ClassificationType
                        | undefined)
                    : null
                }
              />
            </span>
            <span aria-hidden="true">•</span>
            <span className="inline-flex items-center gap-1.5">
              <span className="text-xs text-ds-text-500">Nivel risc:</span>
              {riskData?.global_cyfun_level ? (
                <CyFunLevelBadge
                  level={riskData.global_cyfun_level}
                  size="sm"
                />
              ) : (
                <Badge className="bg-ds-border-200 text-ds-text-500 text-xs px-2 py-0.5">
                  -
                </Badge>
              )}
            </span>
          </div>
        </div>

        {entityData && decizieQuery.data && (
          <div className="mb-5">
            <SectorMismatchAlert
              notificareSectors={getNotificareSectorNames(entityData)}
              decizieSectors={getDecizieSectorNames(decizieQuery.data)}
            />
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-[3fr_2fr] items-start gap-5">
          {/* Row 1: Notificare */}
          <ExpandableDashboardCard
            icon={FileText}
            title="1. Formular Notificare"
            subtitle="Inregistrare entitate"
            status={step1Status}
            progress={
              steps.step1.status !== "not_started" ? step1Progress : undefined
            }
            isExpanded={expandedRows.notificare}
            onToggle={() => toggleRow("notificare")}
            onClick={
              steps.step1.status === "not_started"
                ? () => showOnboardingModal()
                : undefined
            }
          >
            {steps.step1.status !== "not_started" && entityData && (
              <NotificareDetails
                entityData={entityData}
                sectors={sectors}
                onDetailsClick={showOnboardingModal}
                notificationChecks={notificationChecks}
              />
            )}
          </ExpandableDashboardCard>

          <ExpandableDashboardCard
            icon={FileSignature}
            title="Decizie Inregistrare"
            subtitle="Raspuns DNSC"
            status={decizieStatus}
            compact
            isExpanded={expandedRows["decizie-notificare"]}
            onToggle={() => toggleRow("decizie-notificare")}
            onClick={
              notificationPdfStatus === "completed"
                ? () => navigate("/onboarding/decizie-inregistrare")
                : undefined
            }
            headerClassNames={
              notificationPdfStatus === "completed" ? "md:pb-[56px]" : ""
            }
          >
            {decizieQuery.data ? (
              <DecizieInregistrareDetails decizie={decizieQuery.data} />
            ) : undefined}
          </ExpandableDashboardCard>

          {/* Row 2: Efectul Perturbator */}
          {showEfectPerturbatorCard && (
            <ExpandableDashboardCard
              icon={Zap}
              title="Efectul Perturbator"
              subtitle="Art. 9 OUG 155/2024"
              status={efectPerturbatorCardStatus}
              isExpanded={expandedRows["efect-perturbator"]}
              onToggle={() => toggleRow("efect-perturbator")}
              onClick={
                shouldEfectPerturbatorNavigate
                  ? () => showImpactAnalysisModal()
                  : undefined
              }
            >
              {hasCompletedResult && entityData?.impact_analysis && (
                <ImpactResultsTable
                  impactData={entityData.impact_analysis}
                  showEditButton={isImpact}
                />
              )}
            </ExpandableDashboardCard>
          )}
          {showEfectPerturbatorCard && <div className="hidden lg:block" />}

          {/* Row 3: Risc */}
          <ExpandableDashboardCard
            icon={Target}
            title="2. Evaluare Nivel Risc"
            subtitle="Analiza de risc"
            status={step3Status}
            progress={
              steps.step3.status !== "not_started" ? step3Progress : undefined
            }
            isExpanded={expandedRows.risc}
            onToggle={() => toggleRow("risc")}
            onClick={
              shouldRiscNavigate && currentSessionId
                ? () => showRiskAnalysisModal()
                : undefined
            }
          >
            {!shouldRiscNavigate &&
              (isNotificareCompleted && riskData && currentSessionId ? (
                <EvaluareRiscDetails
                  riskData={riskData}
                  sectors={sectors}
                  sessionId={currentSessionId}
                  onDetailsClick={showRiskAnalysisModal}
                />
              ) : (
                <ComingSoonPlaceholder
                  title="Neinceput"
                  message="Finalizati Formularul de Notificare pentru a incepe evaluarea de risc."
                />
              ))}
          </ExpandableDashboardCard>

          <ExpandableDashboardCard
            icon={FileCheck}
            title="Decizie Analiza Risc"
            subtitle="Validare DNSC"
            status={
              steps.step3.status === "not_started"
                ? "neinceput"
                : "in-asteptare"
            }
            compact
            isExpanded={expandedRows["decizie-risc"]}
            onToggle={() => toggleRow("decizie-risc")}
            headerClassNames={
              steps.step3.status !== "not_started" ? "md:pb-[56px]" : ""
            }
          >
            <ComingSoonPlaceholder
              title="In asteptare"
              message="Decizia va fi disponibila dupa finalizarea analizei de risc."
            />
          </ExpandableDashboardCard>

          {/* Row 3: Maturitate */}
          <ExpandableDashboardCard
            icon={BarChart3}
            title="3. Evaluare Maturitate"
            subtitle="CyFun Assessment"
            status={maturityStatus}
            progress={maturityProgress}
            locked={!isRiskCompleted}
            note={
              !isRiskCompleted
                ? "Disponibil după finalizarea analizei de risc"
                : undefined
            }
            isExpanded={expandedRows.maturitate}
            onToggle={() => toggleRow("maturitate")}
            onClick={
              isRiskCompleted
                ? () => navigate("/evaluare-maturitate")
                : undefined
            }
          >
            {!isRiskCompleted && (
              <ComingSoonPlaceholder
                title="Blocat"
                message="Finalizați analiza de risc pentru a accesa evaluarea de maturitate."
              />
            )}
          </ExpandableDashboardCard>

          {/* Full-width: Plan Remediere */}
          <ExpandableDashboardCard
            icon={Wrench}
            title="4. Plan Remediere"
            subtitle="Actiuni corective"
            status="blocat"
            locked
            note="Disponibil dupa finalizarea evaluarii de maturitate"
            fullWidth
            isExpanded={false}
            onToggle={() => {}}
          />
        </div>
      </div>
    </div>
  );
}

export default StatusEntitateDashboard;
